import React, { useState, useEffect, useContext } from 'react'
import * as Routes from 'routes'
import { Modal } from 'reactstrap'
import { API } from 'javascripts/api'

import JobApplicationModalContent from './JobApplicationModalContent'
import JobApplicationCompleteContent from './JobApplicationCompleteContent'
import JobApplicationCommentForm from './JobApplicationCommentForm'
import Session from 'javascripts/layouts/v2/Session'
import TagManager from 'react-gtm-module'
import styles from './JobApplicationButton.module.scss'
import { SessionContext } from '../profile_sheets/information_disclosures/application'

interface JobApplicationButtonProps {
  handleSaveClicked: VoidFunction;
  handleSaving: boolean;
  jobOfferId: number;
  jobOfferTitle: string;
  jobOfferOpen: boolean;
  onButtonContainerMount?: (element: HTMLDivElement | null) => void;
}

const JobApplicationButton: React.FC<JobApplicationButtonProps> = (props) => {
  const [errors, setErrors] = useState<Array<string>>([])
  const [handleSaving, setHandleSaving] = useState<boolean>(false)
  const [jobOfferApplicationExists, setJobOfferApplicationExists] = useState<boolean>(false)
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [favoriteJobOffer, setFavoriteJobOffer] = useState<boolean>(false)
  const [login, setLogin] = useState<boolean>(false)
  const [commentable, setCommentable] = useState<boolean>(false)
  const [csrfToken, setCsrfToken] = useState<string>('')
  const [redirectLogin, setRedirectLogin] = useState<boolean>(false)
  const sessionFromContext = useContext(SessionContext)

  const applySession = (session: Session) => {
    const user = session.user

    setCsrfToken(session.csrf_token)
    setFavoriteJobOffer(session.favorite_job_offer_ids.some(id => (id === props.jobOfferId)))

    if (user) {
      setLogin(true)
      setJobOfferApplicationExists(user.applied_job_offer_ids.some(id => (id === props.jobOfferId)))
      setCommentable(user.job_offer_application_commentable)
    }

    if (session.flash.success) {
      setRedirectLogin(true)
    }
  }

  useEffect(() => {
    if (!sessionFromContext) return

    applySession(sessionFromContext)
  }, [props.jobOfferId, sessionFromContext])

  useEffect(() => {
    const scrollToForm = () => {
      const formElement = document.getElementById('job-offer-comment-form-wrapper')
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth' })
      }
    }

    if (!redirectLogin) return

    const timerId = window.setTimeout(() => {
      commentable ? scrollToForm() : openModal()
    }, 300)

    return () => {
      window.clearTimeout(timerId)
    }
  }, [redirectLogin, commentable])

  const trackJobOfferApplication = () => {
    const current = document.location.href;
    const referrer = document.referrer;
    referrer.includes('recommended_job_offers') && TagManager.dataLayer({
      dataLayer: {
        event: 'job_offer_application_from_recommended_job_offers',
        event_category: 'Conversion',
        event_label: 'job_offer_application'
      }
    })
    current.includes('from=dashboard_recommended_job_offers') && TagManager.dataLayer({
      dataLayer: {
        event: 'job_offer_application_from_dashboard_recommended_job_offers',
        event_category: 'Conversion',
        event_label: 'job_offer_application'
      }
    })
  }

  const handleSaveClicked = async () => {
    setHandleSaving(true)

    try {
      await API.createJobOfferApplication(props.jobOfferId, {})

      trackJobOfferApplication()
      setJobOfferApplicationExists(true)
      setErrors([])
      history.pushState(null, null, Routes.application_completed_job_offer_path(props.jobOfferId))
    } catch (error: any) {
      const errorMessages = error.response?.data?.error || ['エラーが発生しました']
      setErrors(errorMessages)
    } finally {
      setHandleSaving(false)
    }
  }

  const handleFavoriteClicked = () => {
    void $.ajax({
      type: favoriteJobOffer ? 'DELETE' : 'PUT',
      url: Routes.job_offer_favorite_path(props.jobOfferId),
      dataType: 'json',
      contentType: 'application/json',
      headers: { 'X-CSRF-TOKEN': csrfToken },
    }).done(() => {
      setFavoriteJobOffer(!favoriteJobOffer)
    })
  }

  const handleSubmit = () => {
    trackJobOfferApplication();
    setJobOfferApplicationExists(true)
    setModalIsOpen(true)
  }

  const openModal = () => setModalIsOpen(true)
  const closeModal = () => setModalIsOpen(false)

  const job_favorite_button = () => {
    return (
      <a className={`favorite-btn ${favoriteJobOffer ? 'active' : ''}`} onClick={handleFavoriteClicked}>
        気になる
      </a>
    )
  }

  const closed = () => {
    return (
      <>
        <p>この案件は募集を終了しました。</p>
        <div className="job-btn-container">
          <div className="job-btn">募集終了</div>
          {job_favorite_button()}
        </div>
      </>
    )
  }

  const registerable = () => {
    return (
      <>
        <div>
          <p className={styles.registerableMessage}>未登録の方は、次の画面で無料の会員登録に進みます</p>
          <div className="job-btn-container">
            <a className="job-btn" href={Routes.new_user_path({ job_offer_id: props.jobOfferId })} id="ga-job_offers_detail-btn-register">
              この案件に応募する
            </a>
            {job_favorite_button()}
          </div>
        </div>
      </>
    )
  }

  const applied = () => {
    return (
      <>
        <p>事務局からの連絡をお待ちください。</p>
        <div className="job-btn-container">
          <div className="job-btn">応募済み</div>
          {job_favorite_button()}
        </div>

        <Modal isOpen={modalIsOpen} className="interview-modal" centered>
          <JobApplicationCompleteContent
            closeModal={closeModal}
            handleSaving={handleSaving}
          />
        </Modal>
      </>
    )
  }

  const renderCommentable = () => {
    return (
      <>
        <p>応募が集中する場合があります。<br />お早めにご相談ください。</p>
        <div className="job-btn-container">
          <a className="job-btn" href="#job-offer-comment-form-wrapper">応募フォームへ</a>
          {job_favorite_button()}
        </div>
      </>
    )
  }

  const applicable = () => {
    return (
      <>
        <p>応募が集中する場合があります。<br />お早めにご相談ください。</p>
        <div className="job-btn-container">
          <a className="job-btn" onClick={openModal}>応募する</a>
          {job_favorite_button()}
        </div>
        <Modal isOpen={modalIsOpen} fade={false}>
          <JobApplicationModalContent
            jobOfferTitle={props.jobOfferTitle}
            errors={errors}
            handleSaving={handleSaving}
            closeModal={closeModal}
            handleSaveClicked={handleSaveClicked}
          />
        </Modal>
      </>
    )
  }

  const job_application_button = () => {
    if (!props.jobOfferOpen) {
      return (closed())
    }
    if (!sessionFromContext) {
      return null
    }
    if (!login) {
      return (registerable())
    }
    if (jobOfferApplicationExists) {
      return (applied())
    }
    if (commentable) {
      return (renderCommentable())
    }
    return (applicable())
  }

  const comment_form = () => {
    if (!jobOfferApplicationExists && commentable) {
      return (
        <div id="job-offer-comment-form-wrapper" className='job-offer-comment-form-wrapper'>
          <a className={`favorite-btn ${favoriteJobOffer ? 'active' : ''}`} onClick={handleFavoriteClicked}>
            気になるリストに追加をする
          </a>
          <JobApplicationCommentForm
            handleSubmit={handleSubmit}
            jobOfferId={props.jobOfferId}
          />
        </div>
      )
    }
  }

  return (
    <>
      {comment_form()}
      <div 
        ref={props.onButtonContainerMount}
        className='job-offer-application-button-container job-offer-application-button-container-fixed' 
        id='js-job-offer-application-button-container'>
        {job_application_button()}
      </div>
      <p>
        応募後、企業からご面談依頼があった場合のみ事務局からご連絡いたします。
        <br />
        応募から
        <span>5営業日以内</span>
        に事務局から連絡がない場合は見送りとなりますのでご了承ください。
      </p>
    </>
  )
}

export default JobApplicationButton
